import {
    createTUID,
    IElementData,
    evaluateElectricalValue,
    evaluateMechanicalValue,
    IExpressionEvaluationPcbLayoutNodeContext,
    getNodeExpressionEvaluationContext,
    getSubjectExpressionContext,
    normalizeEvaluatedExpression,
    IExpressionEvaluationSchematicSubjectContext,
} from "@buildwithflux/core";
import {isDevEnv, transpileToJavascript} from "@buildwithflux/shared";
import {diff} from "deep-diff";
import {OptionsObject, SnackbarMessage} from "notistack";

import RingBuffer from "../../helpers/RingBuffer";
import {FluxLogger} from "../storage_engine/connectors/LogConnector";

import {SandboxGlobals} from "./api/config/SandboxGlobals";
import FluxAPI from "./api/public/FluxAPI";
import {
    IOutputMetrics,
    ISimulationMetricName,
    ISimulationMetricsHistory,
    PcbLayoutRuleUpdateData,
    PropertyUpdateData,
} from "./behavior.types";
import {CallbackManager} from "./CallbackManager";
import {BehaviorEvent} from "./events";
import {scopeSubElements, scopeSubRoutes} from "./helpers/scopeSubjects";
import {KnownSimulationModelConfig} from "./KnownSimulationModelConfig";
import {inferModelFromPartVersionData} from "./models/inferred/fromPartVersionData";
import {InferredDeviceModel} from "./models/inferred/types";
import {TerminalMeasurements} from "./SimulationModelAdapter";
import {DispatchElementActionCallback, IElementStateProxy, IUserCodeDocumentState} from "./UserCodeRuntime.types";
import {RuntimeData} from "./UserCodeRuntime.worker";

export class Behavior extends CallbackManager {
    public static readonly maximumHistoryLength = 500; // TODO: Move somewhere else?

    public elementData: Readonly<IElementData>;
    public mode?: string;
    public readonly history: ISimulationMetricsHistory = {};

    protected localUserCodeEnvState: Readonly<IUserCodeDocumentState>;

    private readonly fluxApi: FluxAPI;
    private modelConfig?: KnownSimulationModelConfig; // TODO: v2 config?
    private inferredModel?: InferredDeviceModel<KnownSimulationModelConfig>;
    private currentSymbol?: string;
    private readonly userCode?: string;
    private readonly actionDispatcher: DispatchElementActionCallback;
    private eventQueue: BehaviorEvent[];

    /** Output Metrics generated by user code */
    private userOutputMetrics: IOutputMetrics = {};
    /** Output Metrics generated by us (when there is no user generated) */
    private generatedOutputMetrics: IOutputMetrics = {};

    constructor(elementData: Readonly<IElementData>, runtimeData: Readonly<RuntimeData>, eventQueue: BehaviorEvent[]) {
        super();

        this.eventQueue = eventQueue;

        this.localUserCodeEnvState = runtimeData;
        this.actionDispatcher = runtimeData.actionDispatcher;
        this.elementData = elementData;

        const elementStateProxy: IElementStateProxy = {
            elementData: this.elementData,

            subElements: scopeSubElements(runtimeData.flattenedSubjects.elements, this.elementData.uid),
            subRoutes: scopeSubRoutes(runtimeData.flattenedSubjects.routes, this.elementData.uid),

            history: this.history,
            mode: this.mode,

            callbacks: {
                actionDispatcher: (actionResult) => {
                    this.actionDispatcher({
                        action: actionResult,
                        elementUid: this.elementData.uid,
                        documentUid: this.localUserCodeEnvState.documentState.uid,
                        partUid: this.localUserCodeEnvState.documentState.belongs_to_part_uid,
                    });
                },
                userCodeRuntimeError: (error) => this.userCodeRuntimeError(error),
                printApiOutput: (output) => console.log(output), // eslint-disable-line no-console
                setOutputMetrics: (outputNodes) => this.setOutputMetrics(outputNodes),
                enqueueSnackbar: (message, options) => this.notify(message, options),
                changeSymbol: (symbol) => this.changeSymbol(symbol),
                getSymbol: this.getSymbol,
                setModel: (config) => this.setModel(config),
                getModel: this.getModel,

                // register 'on' callbacks
                registerOnLoadCallback: (onCallback) => this.registerOnCallback(this.loadCallbacks, onCallback),
                registerOnUnloadCallback: (onCallback) => this.registerOnCallback(this.unloadCallbacks, onCallback),
                registerOnSetupCallback: (onCallback) => this.registerOnCallback(this.setupCallbacks, onCallback),
                registerOnBeforeStepCallback: (onCallback) =>
                    this.registerOnCallback(this.beforeStepCallbacks, onCallback),
                registerOnAfterStepCallback: (onCallback) =>
                    this.registerOnCallback(this.afterStepCallbacks, onCallback),
                registerOnTearDownCallback: (onCallback) => this.registerOnCallback(this.teardownCallbacks, onCallback),
                registerOnInputChangeCallback: (onCallback) =>
                    this.registerOnCallback(this.inputChangeCallbacks, onCallback),
                registerOnPropertyChangeCallback: (onCallback) =>
                    this.registerOnCallback(this.propertyChangeCallbacks, onCallback),
                registerOnPcbLayoutRuleChangeCallback: (onCallback) =>
                    this.registerOnCallback(this.pcbLayoutRuleChangeCallbacks, onCallback),

                // register 'once' callbacks
                registerOnceLoadCallback: (onCallback) => this.registerOnceCallback(this.loadCallbacks, onCallback),
                registerOnceUnloadCallback: (onCallback) => this.registerOnceCallback(this.unloadCallbacks, onCallback),
                registerOnceSetupCallback: (onCallback) => this.registerOnceCallback(this.setupCallbacks, onCallback),
                registerOnceBeforeStepCallback: (onCallback) =>
                    this.registerOnceCallback(this.beforeStepCallbacks, onCallback),
                registerOnceAfterStepCallback: (onCallback) =>
                    this.registerOnceCallback(this.afterStepCallbacks, onCallback),
                registerOnceTearDownCallback: (onCallback) =>
                    this.registerOnceCallback(this.teardownCallbacks, onCallback),
                registerOnceInputChangeCallback: (onCallback) =>
                    this.registerOnceCallback(this.inputChangeCallbacks, onCallback),
                registerOncePropertyChangeCallback: (onCallback) =>
                    this.registerOnceCallback(this.propertyChangeCallbacks, onCallback),
                registerOncePcbLayoutRuleChangeCallback: (onCallback) =>
                    this.registerOnceCallback(this.pcbLayoutRuleChangeCallbacks, onCallback),

                // unregister callbacks
                unregisterLoadCallback: (onCallback) => this.unregisterCallback(this.loadCallbacks, onCallback),
                unregisterUnloadCallback: (onCallback) => this.unregisterCallback(this.unloadCallbacks, onCallback),
                unregisterSetupCallback: (onCallback) => this.unregisterCallback(this.setupCallbacks, onCallback),
                unregisterBeforeStepCallback: (onCallback) =>
                    this.unregisterCallback(this.beforeStepCallbacks, onCallback),
                unregisterAfterStepCallback: (onCallback) =>
                    this.unregisterCallback(this.afterStepCallbacks, onCallback),
                unregisterTearDownCallback: (onCallback) => this.unregisterCallback(this.teardownCallbacks, onCallback),
                unregisterInputChangeCallback: (onCallback) =>
                    this.unregisterCallback(this.inputChangeCallbacks, onCallback),
                unregisterPropertyChangeCallback: (onCallback) =>
                    this.unregisterCallback(this.propertyChangeCallbacks, onCallback),
                unregisterPcbLayoutRuleChangeCallback: (onCallback) =>
                    this.unregisterCallback(this.pcbLayoutRuleChangeCallbacks, onCallback),
            },
        };

        this.fluxApi = new FluxAPI(runtimeData, elementStateProxy);

        try {
            this.triggerLoadCallbacks({});
        } catch (error) {
            this.userCodeRuntimeError(error);
        }

        this.userCode = this.elementData.part_version_data_cache.behaviour;

        this.setup();
    }

    public userCodeRuntimeError(error: unknown) {
        const objectName = this.elementData.label || this.elementData.part_version_data_cache.name;

        let message = error;
        let stack = "";
        if (error instanceof Error) {
            message = error.message;
            stack = error.stack || "";
        }

        this.fluxApi.print(`${objectName} has caused the following error: ${message}`, isDevEnv() ? stack : undefined);
        this.fluxApi.print("Stopping User code runtime execution and Simulator! Please resolve errors.");

        this.eventQueue.push({
            type: "userCodeRuntimeError",
            message: `User code runtime stopped due to an error in ${objectName}`,
            options: {
                variant: "error",
                preventDuplicate: true,
            },
        });
        this.emitUserCodeError();
    }

    public beforeStep(eventContext: Readonly<SimulatorLifeCycleEventContext>) {
        try {
            this.triggerBeforeStepCallbacks(eventContext);
        } catch (error) {
            this.userCodeRuntimeError(error);
        }
    }

    public afterStepFinished(
        eventContext: Readonly<SimulatorLifeCycleEventContext>,
        terminalData: Readonly<TerminalMeasurements>,
    ) {
        this.writeSimulationOutputMetricsHistory(terminalData);

        try {
            this.triggerAfterStepCallbacks(eventContext);

            if (this.inferredModel) {
                // If we have an inferredModel we want to write the output metrics ourselves
                const outputMetrics: OutputNode[] = [];
                for (const inferredTerminalData of Object.values(this.inferredModel.terminalAliasResolutions)) {
                    const measurements = terminalData.get(inferredTerminalData.terminalUid);
                    if (!measurements) continue;

                    const voltageNode = this.fluxApi.createOutputNode({
                        name: `V${inferredTerminalData.terminalDesignator}`,
                        value: measurements[ISimulationMetricName.volts],
                        unit: "V",
                        displayInline: false,
                    });
                    outputMetrics.push(voltageNode);

                    if (measurements[ISimulationMetricName.current] !== undefined) {
                        const currentNode = this.fluxApi.createOutputNode({
                            name: `I${inferredTerminalData.terminalDesignator}`,
                            value: measurements[ISimulationMetricName.current],
                            unit: "A",
                            displayInline: false,
                        });
                        outputMetrics.push(currentNode);
                    }
                }

                this.setOutputMetrics(outputMetrics, true);
            } else if (Object.keys(this.userOutputMetrics).length === 0) {
                // If we aren't inferred and we have no user generated output metrics,
                // we want to write the output metrics ourselves
                const outputMetrics: OutputNode[] = [];
                for (const [terminalUid, measurements] of terminalData) {
                    const terminal = this.elementData.part_version_data_cache.terminals[terminalUid];
                    if (!terminal) continue;
                    if (!terminal.name) continue;
                    // `+` is not a valid character for document configs, so we replace it with `p` to avoid errors
                    const name = terminal.name.replace("+", "p");
                    const voltageNode = this.fluxApi.createOutputNode({
                        name: `V${name}`,
                        value: measurements[ISimulationMetricName.volts],
                        unit: "V",
                        displayInline: false,
                    });
                    outputMetrics.push(voltageNode);

                    if (measurements[ISimulationMetricName.current] !== undefined) {
                        const currentNode = this.fluxApi.createOutputNode({
                            name: `I${name}`,
                            value: measurements[ISimulationMetricName.current],
                            unit: "A",
                            displayInline: false,
                        });
                        outputMetrics.push(currentNode);
                    }
                }

                this.setOutputMetrics(outputMetrics, true);
            }
        } catch (error) {
            this.userCodeRuntimeError(error);
        }
    }

    public setModel(modelConfig: KnownSimulationModelConfig | undefined) {
        // If modelConfig is already set, check if there is a difference
        // If there is, queue an AlterModel event
        if (this.modelConfig && modelConfig) {
            const difference = diff(this.modelConfig, modelConfig);
            if (!difference) return; // no difference
            // We want to ignore diffs that only contain removals, since they are not useful
            const filteredDifference = difference.filter((d) => d.kind !== "D");
            if (filteredDifference.length === 0) return; // no difference
            this.eventQueue.push({
                type: "alterModel",
                elementUid: this.elementData.uid,
                model: modelConfig,
                diff: filteredDifference,
            });
        }

        this.modelConfig = modelConfig;
    }

    public getModel() {
        return this.modelConfig;
    }

    public setOutputMetrics(outputNodes: ReadonlyArray<OutputNode>, generated: boolean = false) {
        const outputMetrics = generated ? this.generatedOutputMetrics : this.userOutputMetrics;

        outputNodes.forEach((outputNode) => {
            outputMetrics[outputNode.name] = {
                uid: outputNode.id,
                key: outputNode.name,
                parentUid: outputNode.parentUid,
                name: outputNode.name,
                value: outputNode.value,
                unit: outputNode.unit,
                displayInline: outputNode.displayInline,
                timeDependent: true,
            };
        });

        this.eventQueue.push({
            type: "outputMetrics",
            elementUid: this.elementData.uid,
            metrics: outputMetrics,
        });
    }

    public changeSymbol(symbol: string) {
        if (symbol === this.currentSymbol) return; // no change
        this.eventQueue.push({
            type: "changeSymbol",
            elementUid: this.elementData.uid,
            symbol,
        });
        this.currentSymbol = symbol;
    }

    public getSymbol(): string {
        return this.currentSymbol ?? "";
    }

    public inputControlUpdate(controlConfig: IControlConfig, value: ControlPositionData) {
        const eventContext: InputControlChangeEvent = {
            control_uid: controlConfig.uid,
            control_name: controlConfig.name,
            value,
        };

        try {
            this.triggerInputChangeCallbacks(eventContext);
        } catch (error) {
            this.userCodeRuntimeError(error);
        }
    }

    public propertyUpdate(propertyUpdateData: PropertyUpdateData) {
        const rawValue = propertyUpdateData.value;
        let evaluatedValue = rawValue;
        if (typeof rawValue === "string") {
            const elementContext: IExpressionEvaluationSchematicSubjectContext = {
                project: this.localUserCodeEnvState.projectExpressionContext,
                this: getSubjectExpressionContext(
                    {
                        project: this.localUserCodeEnvState.projectExpressionContext,
                    },
                    this.elementData,
                ),
            };

            const evaluatedExpression = evaluateElectricalValue(rawValue, true, elementContext);
            evaluatedValue = normalizeEvaluatedExpression(evaluatedExpression, false, false);
        }

        const eventContext: PropertyChangeEvent = {
            property_uid: propertyUpdateData.uid,
            property_name: propertyUpdateData.name,
            rawValue,
            value: evaluatedValue,
        };

        try {
            this.triggerPropertyChangeCallbacks(eventContext);
        } catch (error) {
            this.userCodeRuntimeError(error);
        }
    }

    public pcbLayoutRuleUpdate(updateData: PcbLayoutRuleUpdateData) {
        if (updateData.disabled) return; // don't trigger callbacks for disabled rules

        const rawValue = updateData.value;
        let evaluatedValue = rawValue;
        if (typeof rawValue === "string") {
            const pcbLayoutNode = this.localUserCodeEnvState.documentState.pcbLayoutNodes[this.elementData.uid];

            const pcbLayoutNodeContext: IExpressionEvaluationPcbLayoutNodeContext = {
                project: this.localUserCodeEnvState.projectExpressionContext,
                this:
                    pcbLayoutNode &&
                    getNodeExpressionEvaluationContext(
                        pcbLayoutNode,
                        this.localUserCodeEnvState.documentState,
                        FluxLogger,
                    ),
            };

            const evaluatedExpression = evaluateMechanicalValue(rawValue, true, pcbLayoutNodeContext);
            evaluatedValue = normalizeEvaluatedExpression(evaluatedExpression, false, false) || "";
        }

        const eventContext: PcbLayoutRuleChangeEvent = {
            pcb_layout_rule_uid: updateData.uid,
            pcb_layout_rule_key: updateData.key,
            rawValue,
            value: evaluatedValue,
        };

        try {
            this.triggerPcbLayoutRuleChangeCallbacks(eventContext);
        } catch (error) {
            this.userCodeRuntimeError(error);
        }
    }

    public notify(message: SnackbarMessage, options?: OptionsObject) {
        this.eventQueue.push({
            type: "enqueueSnackbar",
            message,
            options,
        });
    }

    private emitUserCodeError() {
        this.eventQueue.push({
            type: "userCodeError",
            error: {
                message: `The ${this.elementData.part_version_data_cache.name} model crashed the simulator.`,
                document_import_uid: this.elementData.part_version_data_cache.document_import_uid,
            },
        });
    }

    private writeSimulationOutputMetricsHistory(terminalData: Readonly<TerminalMeasurements>) {
        for (const [terminalUid, terminalMetrics] of terminalData) {
            const uniqueTerminalId = createTUID([this.elementData.uid, terminalUid]);
            if (!this.history[uniqueTerminalId]) {
                this.history[uniqueTerminalId] = new RingBuffer(Behavior.maximumHistoryLength);
            }
            this.history[uniqueTerminalId]!.push(terminalMetrics);
        }
    }

    private setup() {
        if (!this.userCode) {
            // If no userCode, check if we can infer the model
            this.inferModel();
            return;
        }

        const eventContext: SimulatorLifeCycleEventContext = {
            step: 0,
            time_since_simulation_start: 0, // FIXME
            time_since_last_step: 0, // FIXME
            step_size_time: 0, // FIXME
        };

        this.runCode();
        try {
            this.triggerSetupCallbacks(eventContext);
            // If the user didn't set a model, try to infer it
            if (this.modelConfig === undefined) {
                this.inferModel();
            }
        } catch (error) {
            this.userCodeRuntimeError(error);
        }
    }

    private runCode() {
        if (!this.userCode) return;
        try {
            const code = compileCode(this.userCode);
            const context = {flux: this.fluxApi};
            return code(context);
        } catch (error) {
            this.emitUserCodeError();
        }
    }

    private inferModel() {
        if (this.modelConfig) return; // don't overwrite an existing model
        const maybeInferredModel = inferModelFromPartVersionData(this.elementData.part_version_data_cache);
        if (maybeInferredModel.success) {
            this.setModel(maybeInferredModel.model);
            this.inferredModel = maybeInferredModel;
        }
    }
}

function compileCode(sourceCode: string) {
    const compiler = require("@nx-js/compiler-util");

    const transpiledSourceCode = transpileToJavascript(sourceCode);
    const cleanSourceCode = catchSandboxRuntimeErrors(transpiledSourceCode);
    const jsSourceCode = asyncWrapper(cleanSourceCode);

    compiler.expose.apply(undefined, SandboxGlobals);
    return compiler.compileCode(jsSourceCode);
}

function catchSandboxRuntimeErrors(sourceCode: string) {
    return `try {${sourceCode}} catch (error) {flux.throwRuntimeError(error);}`;
}

function asyncWrapper(sourceCode: string) {
    return `
        const $asyncWrapperFunction = async () => {
            ${sourceCode}
        }
        return $asyncWrapperFunction()
    `;
}
